import { Heart, MessageCircle, Trash2 } from "lucide-react";
import { useState } from "react";
import { CurrentUser, Post } from "../../types/types";
import { formatTimeAgo } from "../../utils/helpers";
import CommentList from "./posts/comments/CommentList";
import CommentInput from "./posts/comments/CommentInput";

interface PostCardProps {
  post: Post;
  currentUser: CurrentUser;
  isDeleting: boolean;
  onLike: (postId: number) => void;
  onDelete: (postId: number) => void;
}

const PostCard: React.FC<PostCardProps> = ({
  post,
  currentUser,
  isDeleting,
  onLike,
  onDelete,
}) => {
  const author = post.user?.name || `User ${post.user_id}`;
  const isOwnPost = post.user_id === currentUser.id;
  const [showComments, setShowComments] = useState(false);

  return (
    <div
      className={`bg-white rounded-2xl border border-gray-200 overflow-hidden transition-all duration-300 ease-out ${
        isDeleting
          ? "opacity-0 scale-95 -translate-y-4 h-0 py-0 my-0 border-transparent"
          : "opacity-100 scale-100 translate-y-0"
      }`}
      style={{ marginBottom: isDeleting ? 0 : "1.5rem" }}
    >
      {/* Post Header */}
      <div className="flex items-center justify-between p-4 pb-3">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-full bg-gradient-to-br from-orange-400 to-pink-500 flex items-center justify-center text-white font-bold text-xl shadow-sm">
            {author.charAt(0).toUpperCase() || "?"}
          </div>
          <div>
            <h3 className="font-semibold text-gray-900">{author}</h3>
            <p className="text-sm text-gray-500">
              {formatTimeAgo(post.created_at)}
            </p>
          </div>
        </div>

        {isOwnPost && (
          <button
            onClick={() => onDelete(post.id)}
            disabled={isDeleting}
            className="p-2 rounded-full hover:bg-red-50 transition-colors"
            aria-label="Delete post"
          >
            <Trash2 className="w-5 h-5 text-red-500" />
          </button>
        )}
      </div>

      {/* Post Content */}
      <div className="px-4 pb-4">
        <p className="text-gray-800 whitespace-pre-wrap leading-relaxed text-[15px]">
          {post.body}
        </p>
      </div>

      {/* Actions Bar */}
      <div className="border-t border-gray-100 px-4 py-3">
        <div className="flex items-center gap-10">
          {/* Like Button */}
          <button
            onClick={() => onLike(post.id)}
            className={`flex items-center gap-2 transition-all duration-200 ${
              post.is_liked
                ? "text-red-600"
                : "text-gray-600 hover:text-red-500 active:scale-95"
            }`}
            aria-label={post.is_liked ? "Unlike post" : "Like post"}
          >
            <Heart
              className="w-5 h-5 transition-all duration-200"
              fill={post.is_liked ? "currentColor" : "none"}
              strokeWidth={post.is_liked ? 2.5 : 2}
            />
            <span className="text-sm font-medium">{post.likes_count}</span>
          </button>

          {/* Comments Button */}
          <button
            onClick={() => setShowComments(!showComments)}
            className="flex items-center gap-2 text-gray-600 hover:text-orange-600 transition-colors active:scale-95"
            aria-expanded={showComments}
            aria-label="Toggle comments"
          >
            <MessageCircle className="w-5 h-5" />
            <span className="text-sm font-medium">{post.comments_count}</span>
          </button>
        </div>

        {/* Comments Section – appears when toggled */}
        {showComments && (
          <div className="mt-4 pt-3 border-t border-gray-100 animate-fade-in">
            <CommentList postId={post.id} currentUser={currentUser} />

            <div className="mt-4">
              <CommentInput
                post={post}
                currentUser={currentUser}
                onCancel={() => setShowComments(false)}
                autoFocusOnMount={true}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PostCard;
